// NOTE: We deliberately use React.useEffect, etc instead of importing so tests work
import React, { useState, type RefObject } from 'react';
import { Modal, OverlayTrigger, Tooltip, Button as ReactButton } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle, faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import Toggle from 'components/toggle';
import NumberInput from 'components/form/managed_input/number_input';
import Button from 'shared/button';
import { get } from 'lodash';
import { post as httpPost, put as httpPut } from 'helpers/api';

// TODO: Define stricter types for config and nested objects
interface AreaAnalyticsConfig {
  categories?: Record<string, boolean>;
  radiusRings?: {
    visible?: boolean;
    radii?: number[];
  };
  driveTime?: {
    visible?: boolean;
    durations?: number[];
    type?: string;
  };
  [key: string]: any;
}

interface AreaAnalyticsSettingsProps {
  alphamapHost: string;
  display: boolean;
  iframeTimeoutMs: number;
  iframeUrl: string;
  failureEvent: string;
  updatePath: string;
  unresponsivePath: string;
}

export default function AreaAnalyticsSettings(props: AreaAnalyticsSettingsProps) {
  // TODO: use classes instead
  const borderStyle = '1px solid var(--neutral-200, #DBDDDE)';
  const borderStylesWithoutTop = {
    borderLeft: borderStyle,
    borderRight: borderStyle,
    borderBottom: borderStyle
  };
  const cardBackgroundStyle = 'var(--neutral-100, #F3F5F6)';
  const numberInputStyles = { width: '4em', marginRight: '5px' };
  const uiMaxWidth = '1100px';

  const radiusRingsRadiiDefaults = { minValue: 1, maxValue: 100 };
  const driveTimeDurationsDefaults = { minValue: 1, maxValue: 60 };

  const iframeRef: RefObject<HTMLIFrameElement> = React.useRef(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [iframeSrc, setIframeSrc] = useState<string>(''); // delay loading
  const [iframeHeight, setIframeHeight] = useState<number>(100);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveStatus, setSaveStatus] = useState<'success' | 'failure' | null>(null);
  const [failureErrorMessage, setFailureErrorMessage] = useState<string | null>(null);
  const [config, setConfig] = useState<AreaAnalyticsConfig>({});
  const [display, setDisplay] = useState<boolean>(props.display);
  const [showDisplayTurnedOffModal, setShowDisplayTurnedOffModal] = useState<boolean>(false);

  // BEGIN IFRAME TIMEOUT HANDLING
  const iframeTimeoutMs = props.iframeTimeoutMs < 30000 ? 30000 : props.iframeTimeoutMs;
  const iframeTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const resizeReceivedRef = React.useRef(false);

  const markResizeReceived = () => { resizeReceivedRef.current = true; };
  const resetResizeReceived = () => { resizeReceivedRef.current = false; };
  const wasResizeReceived = () => resizeReceivedRef.current;

  const clearIframeTimeout = () => {
    if (iframeTimeoutRef.current) {
      clearTimeout(iframeTimeoutRef.current);
      iframeTimeoutRef.current = null;
    }
  };
  const setIframeTimeout = (callback: () => void) => {
    clearIframeTimeout();
    iframeTimeoutRef.current = setTimeout(callback, iframeTimeoutMs);
  };
  // END IFRAME TIMEOUT HANDLING

  const handleIframeFailure = () => {
    window.dispatchEvent(new CustomEvent(props.failureEvent));
  }

  React.useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      if (!iframeRef.current) return;

      const data = e.data;
      if (!data.type || e.origin !== alphaMapUrl()) return;

      if (e.data.type === 'alphamap-resize') {
        markResizeReceived();
        clearIframeTimeout();
        setIframeHeight(Math.floor(e.data.payload.height));
      } else if (e.data.type === 'alphamap-configuration-bootstrap') {
        const driveTimeObj = (e.data.payload.driveTime = e.data.payload.driveTime || {});
        // Set a default because alphamap isn't always sending a value; TODO: they should always send a value
        driveTimeObj.type = driveTimeObj.type || 'inbound';

        setConfig(prevConfig => ({ ...prevConfig, ...e.data.payload }));
        setIsLoading(false);
      }
    };

    window.addEventListener('message', handleMessage);

    if (iframeRef.current) {
      setIframeSrc(props.iframeUrl);
    }

    resetResizeReceived();
    setIframeTimeout(() => {
      if (!wasResizeReceived()) {
        httpPost(props.unresponsivePath, { iframeUrl: props.iframeUrl }).catch(() => {});
        handleIframeFailure();
      }
    });

    return () => {
      window.removeEventListener('message', handleMessage);
      clearIframeTimeout();
    };
  }, [props.iframeUrl]);

  const applyConfigChange = (newConfig: AreaAnalyticsConfig) => {
    guardDisplay(newConfig, display);
    postConfigToIframe(newConfig);
    setConfig(newConfig);
  };

  const handleSave = async () => {
    setIsSaving(true);

    try {
      await httpPut(props.updatePath, { area_analytics: { display: display, map_settings: config } });

      // TODO: We may want alphamap to send an ack message. Once that's implemented, we can make this a separate promise
      postSaveToIframe();
      setSaveStatus('success');
    } catch (e: any) {
      setSaveStatus('failure');

      const httpErrorMessage = get(e, 'response.data.error');
      if (httpErrorMessage) {
        setFailureErrorMessage(httpErrorMessage);
      }
    } finally {
      setIsSaving(false);
      setTimeout(() => {
        setSaveStatus(null);
        setFailureErrorMessage(null);
      }, 5000);
    }
  };

  const alphaMapUrl = (): string => {
    if (props.alphamapHost.startsWith('https')) {
      return props.alphamapHost;
    }

    return `https://${props.alphamapHost}`;
  };

  const postConfigToIframe = (newConfig: AreaAnalyticsConfig) => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      iframeRef.current.contentWindow.postMessage({
        type: 'alphamap-configuration-change',
        payload: newConfig
      }, alphaMapUrl());
    }
  };

  const postSaveToIframe = () => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      iframeRef.current.contentWindow.postMessage({
        type: 'alphamap-configuration-save',
        payload: config // this must be sent for now, or the data will be corrupted
      }, alphaMapUrl());
    }
  };

  const getCategory = (category: string): boolean => get(config, `categories.${category}`, false);

  const toggleCategory = (category: string) => {
    const oldVal = get(config, `categories.${category}`, false);
    applyConfigChange({ ...config, categories: { ...config.categories, [category]: !oldVal } });
  };

  const getBoolean = (key: string): boolean => get(config, key, false);

  const setRadiusVisible = (visible: boolean) => {
    applyConfigChange({ ...config, radiusRings: { ...config.radiusRings, visible: !!visible } });
  };

  const setRadiusRing = (index: number, value: number) => {
    const origRadii = get(config, 'radiusRings.radii', []);
    applyConfigChange({
      ...config,
      radiusRings: { ...config.radiusRings, radii: origRadii.toSpliced(index, 1, value) }
    });
  };

  const setDriveTimeVisible = (visible: boolean) => {
    applyConfigChange({ ...config, driveTime: { ...config.driveTime, visible: !!visible } });
  };

  const setDriveTimeDuration = (index: number, value: number) => {
    const origDurations = get(config, 'driveTime.durations', []);
    applyConfigChange({
      ...config,
      driveTime: { ...config.driveTime, durations: origDurations.toSpliced(index, 1, value) }
    });
  };

  const isDriveTimeInbound = (): boolean => get(config, 'driveTime.type') === 'inbound';
  const isDriveTimeOutbound = (): boolean => get(config, 'driveTime.type') === 'outbound';

  const setDriveTimeDirection = (direction: string) => {
    if (direction === 'inbound' || direction === 'outbound') {
      applyConfigChange({ ...config, driveTime: { ...config.driveTime, type: direction } });
    }
  };

  const noOp = () => { };

  const renderHeader = () => {
    return (
      <div className="d-flex justify-content-between">
        <h4>Area Analytics Settings</h4>
        <div className="d-flex align-items-center justify-content-between" style={{ gap: '1rem' }}>
          {saveStatus === 'success' &&
            <div className="text-success">Saved successfully</div>
          }
          {saveStatus === 'failure' &&
            <div className="text-danger" data-testid="failure-message">
              Save failed{failureErrorMessage ? `: ${failureErrorMessage}` : ''}
            </div>
          }
          <Button.Primary data-testid="save-button" disabled={isSaving} style={{ fontWeight: 'normal' }}
            onClick={handleSave}
          >
            {isSaving ? 'Saving ...' : 'Save Settings'}
          </Button.Primary>
        </div>
      </div>
    );
  };

  const renderDisplayControl = () => {
    const toolTip = (
      <Tooltip id="display-toggle-info">
        Enabling toggle will show area analytics in relevant sections of marketing materials.
      </Tooltip>
    );
    const infoIcon = (
      <OverlayTrigger overlay={toolTip} placement="top">
        <FontAwesomeIcon className="ms-2 info-icon" icon={faInfoCircle} />
      </OverlayTrigger>
    );

    return (
      <div className="d-flex justify-content-between align-items-center border-top-rounded border-bottom-rounded p-3"
        style={{ border: borderStyle, background: cardBackgroundStyle }}
      >
        <div><span className="bold">Display area analytics on this listing?</span> {infoIcon}</div>
        <div>
          <Toggle checked={display} data-testid="display-toggle" onChange={e => applyDisplayChange(e.target.checked)} />
        </div>
      </div>
    );
  };

  const renderCategoriesCard = () => {
    const categories = [
      [
        { label: 'Age', field: 'Age', testId: 'age-checkbox' },
        { label: 'Gender', field: 'Gender', testId: 'gender-checkbox' },
        { label: 'Population', field: 'Overview', testId: 'overview-checkbox' },
        { label: 'Education', field: 'Education' },
      ],
      [
        { label: 'Race & Ethnicity', field: 'Ethnicity' },
        { label: 'Households', field: 'Households' },
        { label: 'Labor Force', field: 'LaborForce' },
        { label: 'Housing Units', field: 'HousingUnits' },
      ],
      [
        { label: 'Marital Status', field: 'MaritalStatus' },
        { label: 'Household Income', field: 'HouseholdIncome' },
        { label: 'Travel Time to Work', field: 'TravelTimeToWork' },
        { label: 'Types of Housing Units', field: 'TypesOfHousingUnits' },
      ],
      [
        { label: 'Population by Industry', field: 'PopulationByIndustry' },
        { label: 'Transportation to Work', field: 'TransportationToWork' },
        { label: 'Population by Occupation', field: 'PopulationByOccupation' },
        { label: 'Value of Owner Occupied Housing Units', field: 'ValueOfOwnerOccupiedHousingUnits' },
      ]
    ];

    return (
      <div className="d-flex flex-column justify-content-between border-top-rounded border-bottom-rounded"
        style={{ minWidth: 305 }}
      >
        <div className="border-top-rounded border-bottom-rounded p-3"
          style={{ border: borderStyle, background: cardBackgroundStyle }}
        >
          <span className="bold">Select Categories to Display</span>
        </div>

        <div className="d-flex justify-content-between flex-wrap p-3 border-bottom-rounded flex-grow-1"
          style={borderStylesWithoutTop}
        >
          {categories.map((category, index) => (
            <div className="d-flex flex-column p-3" key={index}>
              {category.map(checkbox => (
                <div className="form-check" key={checkbox.field}>
                  <input
                    checked={getCategory(checkbox.field)}
                    className="form-check-input"
                    type="checkbox"
                    id={`category-${checkbox.field}`}
                    onChange={() => toggleCategory(checkbox.field)}
                    {...checkbox.hasOwnProperty('testId') ? { 'data-testid': checkbox.testId } : {}}
                  />
                  <label className="form-check-label" htmlFor={`category-${checkbox.field}`}>
                    {checkbox.label}
                  </label>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderRadiusRingsCard = () => {
    return (
      <div className="d-flex flex-column justify-content-between flex-grow-1 border-top-rounded border-bottom-rounded"
        style={{ minWidth: 305 }}
      >
        <div className="d-flex justify-content-between align-items-center border-top-rounded border-bottom-rounded p-3"
          style={{ border: borderStyle, background: cardBackgroundStyle }}
        >
          <div><span className="bold">Radius Rings</span></div>
          <div>
            <Toggle checked={getBoolean('radiusRings.visible')}
              data-testid="radius-rings-toggle" onChange={e => setRadiusVisible(e.target.checked)}
            />
          </div>
        </div>
        <div className="d-flex flex-column border-bottom-rounded flex-grow-1"
          style={{ ...borderStylesWithoutTop, gap: '2rem', padding: '2rem' }}
        >
          <div className="d-flex flex-column" style={{ gap: '2rem' }}>
            {/* eslint-disable react/jsx-sort-props */}
            <div className="input-group" style={{ maxWidth: '150px' }}>
              <NumberInput
                {...radiusRingsRadiiDefaults}
                className="form-control"
                value={get(config, 'radiusRings.radii[0]')}
                onValueChange={v => setRadiusRing(0, v)}
                onChange={noOp}
              />
              <span className="input-group-text bg-light border-start">Miles</span>
            </div>
            <div className="input-group" style={{ maxWidth: '150px' }}>
              <NumberInput
                {...radiusRingsRadiiDefaults}
                className="form-control"
                value={get(config, 'radiusRings.radii[1]')}
                onValueChange={v => setRadiusRing(1, v)}
                onChange={noOp}
              />
              <span className="input-group-text bg-light border-start">Miles</span>
            </div>
            <div className="input-group" style={{ maxWidth: '150px' }}>
              <NumberInput
                {...radiusRingsRadiiDefaults}
                className="form-control"
                value={get(config, 'radiusRings.radii[2]')}
                onValueChange={v => setRadiusRing(2, v)}
                onChange={noOp}
              />
              <span className="input-group-text bg-light border-start">Miles</span>
            </div>
            {/* eslint-enable react/jsx-sort-props */}
          </div>
        </div>
      </div>
    );
  };

  const renderDriveTimeCard = () => {
    const renderDriveTimeDurations = () => {
      return (
        <div className="d-flex flex-column" style={{ gap: '2rem' }}>
          <div className="d-flex flex-column justify-space-between" style={{ gap: '2rem' }}>
            {/* eslint-disable react/jsx-sort-props */}
            <div className="input-group" style={{ maxWidth: '150px' }}>
              <NumberInput
                {...driveTimeDurationsDefaults}
                className="form-control"
                value={get(config, 'driveTime.durations[0]')}
                onValueChange={v => setDriveTimeDuration(0, v)}
                onChange={noOp}
              />
              <span className="input-group-text bg-light border-start">Min</span>
            </div>
            <div className="input-group" style={{ maxWidth: '150px' }}>
              <NumberInput
                {...driveTimeDurationsDefaults}
                className="form-control"
                value={get(config, 'driveTime.durations[1]')}
                onValueChange={v => setDriveTimeDuration(1, v)}
                onChange={noOp}
              />
              <span className="input-group-text bg-light border-start">Min</span>
            </div>
            <div className="input-group" style={{ maxWidth: '150px' }}>
              <NumberInput
                {...driveTimeDurationsDefaults}
                className="form-control"
                value={get(config, 'driveTime.durations[2]')}
                onValueChange={v => setDriveTimeDuration(2, v)}
                onChange={noOp}
              />
              <span className="input-group-text bg-light border-start">Min</span>
            </div>
            {/* eslint-enable react/jsx-sort-props */}
          </div>
        </div>
      );
    };

    const renderDriveTimeFlags = () => {
      return (
        <div className="d-flex" style={{ gap: '3rem', paddingBottom: '2rem' }}>
          <div className="d-flex flex-column">
            {/* eslint-disable react/jsx-sort-props */}
            <div className="form-check">
              <input className="form-check-input" name="drive-time-direction" type="radio"
                id="drive-time-inbound"
                value="inbound" checked={isDriveTimeInbound()}
                onChange={e => setDriveTimeDirection(e.target.value)}
              />
              <label className="form-check-label" htmlFor="drive-time-inbound">
                Inbound
              </label>
            </div>
            <div className="form-check">
              <input className="form-check-input" name="drive-time-direction" type="radio"
                id="drive-time-outbound"
                value="outbound" checked={isDriveTimeOutbound()}
                onChange={e => setDriveTimeDirection(e.target.value)}
              />
              <label className="form-check-label" htmlFor="drive-time-outbound">
                Outbound
              </label>
            </div>
            {/* eslint-enable react/jsx-sort-props */}
          </div>
        </div>
      );
    };

    return (
      <div className="d-flex flex-column justify-content-between flex-grow-1 border-top-rounded border-bottom-rounded"
        style={{ minWidth: 305 }}
      >
        <div className="d-flex justify-content-between align-items-center border-top-rounded border-bottom-rounded p-3"
          style={{ border: borderStyle, background: cardBackgroundStyle }}
        >
          <div><span className="bold">Drive Time</span></div>
          <div>
            <Toggle checked={getBoolean('driveTime.visible')}
              data-testid="drive-time-toggle" onChange={e => setDriveTimeVisible(e.target.checked)}
            />
          </div>
        </div>
        <div className="d-flex flex-column justify-content-around border-bottom-rounded flex-grow-1"
          style={{ ...borderStylesWithoutTop, padding: '2rem' }}
        >
          {renderDriveTimeFlags()}

          {renderDriveTimeDurations()}
        </div>
      </div>
    );
  };

  const renderDisplayOffModal = () => {
    return (
      <Modal
        data-testid="display-off-modal"
        show={showDisplayTurnedOffModal}
        onHide={() => setShowDisplayTurnedOffModal(false)}
      >
        <div className="text-center p-4">
          <FontAwesomeIcon className="icon-gradient" icon={faExclamationCircle} size="3x"/>

          <h5 className="d-flex justify-content-center mb-4 mt-3">
            <strong>Just a quick heads up</strong>
          </h5>
          <p style={{ lineHeight: 1.5 }}>
            If you decide to turn off both the <b>Drive Time</b> and <b>Radius Rings</b> options,
            <b> Display area analytics</b> will automatically switch off as well. We want
            to ensure you have the best user experience, so feel free to
            customize your settings, but keep in mind that having at least
            one option enabled will keep the display active.
          </p>

          <div className="d-flex justify-content-center mt-4">
            <ReactButton onClick={() => setShowDisplayTurnedOffModal(false)}>
              Okay
            </ReactButton>
          </div>
        </div>
      </Modal>
    );
  };

  const renderIframe = () => {
    // will be resized dynamically
    return (
      <iframe ref={iframeRef} src={iframeSrc} style={{ width: '100%', maxWidth: uiMaxWidth, height: iframeHeight }} />
    );
  };

  // TODO: show error instead of the controls when:
  // - iframeUrl is blank
  // - we don't get an initial message from the iframe in a certain amount of time

  const guardDisplay = (config: AreaAnalyticsConfig, display: boolean) => {
    if (!display) return;
    if (!config) return;
    if (!config.radiusRings || config.radiusRings.visible === true) return;
    if (!config.driveTime || config.driveTime.visible === true) return;

    setDisplay(false);
    setShowDisplayTurnedOffModal(true);
  };

  const applyDisplayChange = (newDisplay: boolean) => {
    setDisplay(newDisplay);
    if (newDisplay) {
      guardDisplay(config, newDisplay);
    }
  };

  return (
    <React.Fragment>
      {isLoading &&
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '500px' }}>
          <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      }
      <div className={`${isLoading ? 'd-none' : 'd-flex'} flex-column align-items-center`}
        data-testid="area-analytics-content" style={{ gap: '2rem' }}
      >
        {/* Use width & maxWidth to ensure the controls & iframe are the same width while being responsive */}

        <div className="d-flex flex-column" style={{ gap: '2rem', width: '100%', maxWidth: uiMaxWidth }}>
          {renderHeader()}

          {renderDisplayControl()}

          {renderCategoriesCard()}

          <div className="d-flex flex-wrap" style={{ gap: '1rem 3rem' }}>
            {renderDriveTimeCard()}

            {renderRadiusRingsCard()}
          </div>
        </div>

        {renderDisplayOffModal()}

        {renderIframe()}
      </div>
    </React.Fragment>
  );
}
